import { Select, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import Styles from './HomePage.module.scss'
import Logo from 'src/assets/logo.png'
import Map, { Source, Layer } from 'react-map-gl/maplibre'
import { ActionBar } from './ActionBar'
import 'maplibre-gl/dist/maplibre-gl.css'
import maplibregl from 'maplibre-gl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCaretDown,
  faLayerGroup,
  faUserCircle,
  faWalking,
} from '@fortawesome/free-solid-svg-icons'
import { useViewModel } from './HomePage.vm'
import { useRef, useState } from 'react'
import {
  DistrictLayerConfigEntries,
  DistrictLayerHoverStyle,
  DistrictLayerStyle,
  DistrictNameEntries,
  Districts,
  MapConfig,
  MapStyleJson,
  MapStyles,
} from '../../meta'
import { ObsMarker } from './ObsMarker'
import { isNil } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { getCurrentUser } from 'src/auth'
import RainJson from './now.json'
import { config } from 'src/config'
import { DataControlPanel } from './DataControlPanel'
import { SideControlButtonGroup } from './SideControlButtonGroup'
import { MapRef } from 'react-map-gl/maplibre'
import { ConfirmLogoutDialog } from 'src/components/dialogs/ConfirmLogoutDialog'
export const HomePage = () => {
  const mapRef = useRef<MapRef | null>(null)
  const [isConfirmLogoutOpen, setConfirmLogoutOpen] = useState(false)
  const [isSigningOut, setSigningOut] = useState(false)
  const photoPath = process.env.REACT_APP_PHOTO_PAHT
  const {
    chartData,
    obsMarkersShown,
    selectedLayer,
    hoveringFeature,
    zoomLevel,
    rotateDegree,
    tiltDegree,
    selectedTime,
    selectedStation,
    mapStyle,
    timeRange,
    playSpeed,
    isPlaying,
    downloadSelection,
    chartZoom,
    setCurrentChartX,
    zoomChartWhenFocus,
    goToLatest,
    toggleDownload,
    onDownloadSelectRange,
    onDownload,
    togglePlay,
    setMapStyle,
    selectStation,
    setSelectedTime,
    onZoom,
    onRotate,
    onTilt,
    clickCtrBtn,
    setHoveringFeature,
    goToDistrict,
    nextPlaySpeed,
    signOut,
  } = useViewModel()
  const navigate = useNavigate()

  const onHover = (id: string) => {
    setHoveringFeature(id)
  }

  const openConfirmLogoutDialog = () => {
    setConfirmLogoutOpen(true)
  }

  const closeConfirmLogoutDialog = () => {
    if (isSigningOut) {
      return
    }

    setConfirmLogoutOpen(false)
  }

  const onConfirmSignOut = async () => {
    setSigningOut(true)

    try {
      await signOut()
    } finally {
      setSigningOut(false)
      setConfirmLogoutOpen(false)
    }
  }

  return (
    <div className={Styles.page}>
      <div className={Styles.map}>
        <Map
          ref={mapRef}
          mapLib={maplibregl}
          initialViewState={MapConfig}
          style={{
            width: '100vw',
            height: '100vh',
            cursor: hoveringFeature ? 'pointer' : 'grab',
          }}
          mapStyle={MapStyleJson[mapStyle]}
          onRotate={e => {
            onRotate(e.target.getBearing())
            onTilt(e.target.getPitch())
          }}
          onZoom={e => onZoom(e.target.getZoom())}
          interactiveLayerIds={Districts}
          onClick={e => goToDistrict(e.features?.[0]?.properties?.areaId as string | null | undefined, mapRef.current ?? undefined)}
          onMouseMove={e => onHover(e.features?.[0]?.properties?.areaId)}
          attributionControl={false}
        >
          {(zoomLevel ?? 0) < 12 &&
            DistrictLayerConfigEntries.map(([d, json], i) => (
              <Source type="geojson" data={json} key={`s-${i}`}>
                <Layer
                  id={d}
                  type="fill"
                  paint={hoveringFeature === d ? DistrictLayerHoverStyle : DistrictLayerStyle}
                />
              </Source>
            ))}
          {selectedLayer === 'cloud' && (
            <Source type="geojson" data={RainJson}>
              <Layer
                id="rain"
                type="fill-extrusion"
                paint={{
                  'fill-extrusion-color': ['get', 'color'],
                  'fill-extrusion-opacity': ['interpolate', ['linear'], ['zoom'], 8, 0.7, 14, 0.1],
                  'fill-extrusion-height': ['*', 50, ['get', 'intensity']],
                }}
              />
            </Source>
          )}
          {selectedLayer !== 'cloud' &&
            obsMarkersShown?.map(data => (
              <ObsMarker
                key={`m-${data.id}-${data.properties.deviceId}`}
                data={data}
                selectedLayer={selectedLayer ?? 'temperature'}
                isSelected={selectedStation?.id === data.id}
                onClick={v => selectStation({ stationId: v.id?.toString() })}
                onCancel={() => selectStation({})}
              />
            ))}
        </Map>
        <SideControlButtonGroup
          selectedLayer={selectedLayer}
          rotateDegree={rotateDegree}
          tiltDegree={tiltDegree}
          onSelectLayer={layer => clickCtrBtn(layer)}
          onZoomIn={() => clickCtrBtn('zoom-in', mapRef.current ?? undefined)}
          onZoomOut={() => clickCtrBtn('zoom-out', mapRef.current ?? undefined)}
          onResetNorth={() => clickCtrBtn('direction', mapRef.current ?? undefined)}
          onLocate={() => clickCtrBtn('locate', mapRef.current ?? undefined)}
          onToggleTilt={() => clickCtrBtn('3D', mapRef.current ?? undefined)}
        />
        {isNil(obsMarkersShown) && (
          <div className={Styles.loadingMask}>
            <Spin
              size="large"
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              spinning
            />
          </div>
        )}
      </div>
      <ActionBar logoSrc={Logo}>
        <Select
          placeholder={
            <>
              <FontAwesomeIcon icon={faLayerGroup} />
              {'Theme'}
            </>
          }
          suffixIcon={<FontAwesomeIcon icon={faCaretDown} />}
          options={MapStyles.map(v => ({ label: v, value: v }))}
          value={null}
          onSelect={v => setMapStyle(v as any)}
        />
        <Select
          placeholder={
            <>
              <FontAwesomeIcon icon={faWalking} />
              {'Go To'}
            </>
          }
          suffixIcon={<FontAwesomeIcon icon={faCaretDown} />}
          popupMatchSelectWidth={false}
          options={DistrictNameEntries.map(([k, v]) => ({
            label: v,
            value: k,
          }))}
          value={null}
          onSelect={(d: unknown) =>
            goToDistrict(typeof d === 'string' ? d : undefined, mapRef.current ?? undefined)
          }
        />
        <Select
          placeholder={
            <>
              <FontAwesomeIcon icon={faUserCircle} />
              {getCurrentUser()}
            </>
          }
          suffixIcon={<FontAwesomeIcon icon={faCaretDown} />}
          disabled={config.shouldSkipAuth}
          options={[{ label: 'Sign-out', value: 'sign-out' }]}
          onSelect={() => openConfirmLogoutDialog()}
        />
      </ActionBar>
      <ConfirmLogoutDialog
        open={isConfirmLogoutOpen}
        loading={isSigningOut}
        onCancel={closeConfirmLogoutDialog}
        onConfirm={onConfirmSignOut}
      />
      <DataControlPanel
        selectedStation={selectedStation}
        selectedLayer={selectedLayer}
        timeStepInMinutes={5}
        chartData={chartData}
        selectedTime={selectedTime}
        chartZoom={chartZoom}
        timeRange={timeRange}
        playSpeed={playSpeed}
        isPlaying={isPlaying}
        downloadSelection={downloadSelection}
        onCloseChart={() => selectStation({})}
        onSelectDevice={deviceId => selectStation({ deviceId })}
        onOpenDashboard={() => selectedStation && navigate(`./stn/${selectedStation.id}`)}
        onSetCurrentChartX={setCurrentChartX}
        onSetSelectedTime={setSelectedTime}
        onTogglePlay={togglePlay}
        onGoToLatest={goToLatest}
        onNextPlaySpeed={nextPlaySpeed}
        onToggleDownload={toggleDownload}
        onDownloadSelectRange={onDownloadSelectRange}
        onDownload={onDownload}
        onZoomChartWhenFocus={zoomChartWhenFocus}
      />
    </div>
  )
}
